import { HttpBackend, HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, shareReplay, tap, throwError } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { SKIP_AUTHENTICATION_REDIRECT } from '../tokens/auth-request-context.tokens';
import type { AuthTokenResponse } from '../types/auth.interfaces';

const ACCESS_TOKEN_STORAGE_KEY = 'smt_access_token';

export interface ValidateSessionOptions {
  skipAuthenticationRedirect?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SmartyAuthService {
  private readonly interceptedHttp = inject(HttpClient);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private readonly authBaseUrl = `${environment.cws}/smarty/auth`;
  private refreshInFlight$?: Observable<string>;

  public exchange(exchangeToken: string): Observable<AuthTokenResponse> {
    return this.rawHttp.post<AuthTokenResponse>(
      `${this.authBaseUrl}/exchange`,
      { exchangeToken },
      { withCredentials: true },
    );
  }

  public refreshSession(): Observable<string> {
    return this.refreshInFlight$ ?? this.createRefreshRequest();
  }

  public validateSession(options: ValidateSessionOptions = {}): Observable<void> {
    return this.interceptedHttp.get<void>(`${this.authBaseUrl}/validate`, {
      context: new HttpContext().set(
        SKIP_AUTHENTICATION_REDIRECT,
        options.skipAuthenticationRedirect ?? false,
      ),
      withCredentials: true,
    });
  }

  public getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  }

  public setAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  }

  public clearAccessToken(): void {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }

  private createRefreshRequest(): Observable<string> {
    const refresh$ = this.rawHttp
      .post<AuthTokenResponse>(`${this.authBaseUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        map(({ token }) => {
          if (!token?.length) {
            throw new Error('Refresh response did not contain an access token.');
          }
          return token;
        }),
        tap((token) => this.setAccessToken(token)),
        catchError((error) => {
          this.clearAccessToken();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.refreshInFlight$ = refresh$;
    return refresh$;
  }
}
